import React, { useState, useEffect, useRef } from "react";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Use the matching version for react-pdf v4.2.0
const pdfjsVersion = pdfjs.version;
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`;

// Matches http(s)://, www., and bare domains like example.com/path
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<>"']+|[a-zA-Z0-9][a-zA-Z0-9-]*\.(?:com|net|org|io|co|edu|gov|info|biz|app|dev|me|us|uk|ca|au|de|fr|jp|cn|in|ru|br|my)(?:\/[^\s<>"']*)?)/gi;

// react-pdf v4 calls customTextRenderer and renders the return value as a React child,
// not innerHTML. So we must return React nodes (not an HTML string) for links to work.
const linkifyText = ({ str, itemIndex }) => {
  if (!str) return str;
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(str)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(str.slice(lastIndex, match.index));
    }
    const raw = match[0];
    const trimMatch = raw.match(/[).,;:!?]+$/);
    const trimmed = trimMatch ? raw.slice(0, -trimMatch[0].length) : raw;
    const trailing = trimMatch ? trimMatch[0] : "";
    const href = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
    nodes.push(
      <a
        key={`lnk-${itemIndex}-${key++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ pointerEvents: "all", cursor: "pointer", color: "inherit" }}
        onClick={(e) => e.stopPropagation()}
      >
        {trimmed}
      </a>
    );
    if (trailing) nodes.push(trailing);
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < str.length) {
    nodes.push(str.slice(lastIndex));
  }
  return nodes.length === 0 ? str : nodes;
};

const PdfViewer = ({ pdfBlob }) => {
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const containerRef = useRef(null);

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
  };

  useEffect(() => {
    // Function to handle resizing
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    // Attach the event listener
    window.addEventListener("resize", handleResize);
    // Initial measurement
    handleResize();
    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100vh",
        overflow: "auto",
        boxSizing: "border-box"
      }}
    >
      <Document
        file={pdfBlob}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={<div>Loading PDF...</div>}
        externalLinkTarget="_blank"
        externalLinkRel="noopener noreferrer"
      >
        {Array.from(new Array(numPages), (el, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            // Use the container width (minus some margin if you prefer).
            // If containerWidth is null (e.g., before mount), you can safely fallback to a fixed value.
            width={containerWidth || 600}
            renderAnnotationLayer={true}
            renderTextLayer={true}
            customTextRenderer={linkifyText}
          />
        ))}
      </Document>
    </div>
  );
};

export default PdfViewer;
